import { toastLocalizedOperationCatcher, toastSuccessMessage } from "./alerts";
import { getLastSectionIdFromCookie } from "./auth";

import { showDeleteConfirmationModal } from "components/modals/DeleteSectionModal";
import { SectionRoleType, SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentSection } from "store/selectors";
import { useHomeUrl } from "utils/hooks/use-home-url";
import { useNavigate } from "utils/routing";

export const SECTION_ROLES = ["student", "section_admin", "instructor"] as SectionRoleType[];

export const updateSection = (section: Partial<SectionType> & { id: string }) => {
  return storeApi.sections.partial_update(section);
};

export const deleteSection = (sectionId: string) => {
  storeApi.sections.destroy(sectionId);
};

export const useUserSectionSettings = () => {
  const { canAuthorSection } = useCapabilities();
  const currentSection = useAppSelector(selectCurrentSection);
  const canSeeAIPanel = canAuthorSection || currentSection?.student_ai_assistant_enabled;
  const hideStaticContentForStudents = currentSection?.hide_static_blocks_on_student_response_page;

  return {
    canSeeAIPanel,
    hideStaticContentForStudents,
  };
};

export const getDefaultSection = (sections: SectionType[]): SectionType => {
  const lastAccessedSectionId = getLastSectionIdFromCookie();
  if (lastAccessedSectionId) {
    const section = sections.find((s) => s.id === lastAccessedSectionId);

    if (section) {
      return section;
    }
  }

  return sections[0];
};

export const useSectionSettingActions = () => {
  const navigate = useNavigate();
  const homeUrl = useHomeUrl();

  const handleSectionDelete = async (section: SectionType) => {
    const sectionId = section.id;
    const sectionTitle = section.title;

    const didDelete = await showDeleteConfirmationModal({ sectionId, sectionTitle });
    if (didDelete) {
      storeApi.sections
        .destroy(sectionId)
        .then(() => {
          toastSuccessMessage(t("settings.delete_section_success"));
          navigate(homeUrl);
        })
        .catch(
          toastLocalizedOperationCatcher("delete_failure", {
            item: t("glossary.section"),
          }),
        );
    }
  };

  return {
    handleSectionDelete,
  };
};

export const getSectionResourceRole = (
  role: SectionRoleType,
): "admin" | "student" | "instructor" | "recording_agent" =>
  role === "section_admin" ? "admin" : role;
